import React from 'react';
import PropTypes from 'prop-types';
import {
  FaBuilding,
  FaEnvelope,
  FaGlobe,
  FaPhone,
  FaUser,
} from 'react-icons/fa';

import InputTextNested from '../../../Components/Form/InputTextNested';
import SelectIndustry from '../../../Components/CompanyForm/Industry';
import PrimaryButtonComponent from '../../../Components/Buttons/PrimaryButton/PrimaryButton.component';

import {
  CompanyInfoContainer,
  CompanyInfoHeader,
  CompanyInfoHeaderIcon,
  CompanyInfoHeaderContent,
  CompanyInfoFields,
  CompanyInfoFieldRow,
  CompanyContactSection,
  CompanyContactHeader,
  CompanyContactOptional,
  CompanyInfoActions,
} from './CompanyInfo.styles';

const CompanyInfo = ({ company, setCompany, setStep }) => {
  const isStepValid =
    company?.companyName?.trim() &&
    company?.phoneOffice?.trim() &&
    company?.email?.trim();

  const handleContinue = (e) => {
    e.preventDefault();

    if (!isStepValid) return;

    const trimmedCompany = {
      ...company,
      companyName: company?.companyName?.trim() || '',
      mainContact: company?.mainContact?.trim() || '',
      email: company?.email?.trim() || '',
      phoneOffice: company?.phoneOffice?.trim() || '',
      website: company?.website?.trim() || '',
      industry: company?.industry?.trim() || '',
    };

    setCompany(trimmedCompany);

    setStep((prev) => prev + 1);
  };

  return (
    <CompanyInfoContainer>
      <CompanyInfoHeader>
        <CompanyInfoHeaderIcon>
          <FaBuilding />
        </CompanyInfoHeaderIcon>

        <CompanyInfoHeaderContent>
          <span>Company Information</span>

          <h2>Tell Us About Your Company</h2>

          <p>
            Start with the basic information we need to create your company
            account. You can add or update additional company details later.
          </p>
        </CompanyInfoHeaderContent>
      </CompanyInfoHeader>

      <CompanyInfoFields>
        <InputTextNested
          title={
            <>
              <FaBuilding />
              Company Name
            </>
          }
          value={company?.companyName}
          name="companyName"
          placeholder="Company Name"
          requiredInput={true}
          nestedValue={setCompany}
          upperValue={company}
          containerClassName="formInputUnderline"
        />

        <CompanyInfoFieldRow>
          <InputTextNested
            title={
              <>
                <FaEnvelope />
                Company Email
              </>
            }
            value={company?.email}
            name="email"
            placeholder="info@company.ca"
            requiredInput={true}
            nestedValue={setCompany}
            upperValue={company}
            containerClassName="formInputUnderline"
          />

          <InputTextNested
            title={
              <>
                <FaPhone />
                Office Phone Number
              </>
            }
            value={company?.phoneOffice}
            name="phoneOffice"
            placeholder="902-555-1234"
            requiredInput={true}
            nestedValue={setCompany}
            upperValue={company}
            containerClassName="formInputUnderline"
          />
        </CompanyInfoFieldRow>

        <CompanyInfoFieldRow>
          <SelectIndustry
            value={company?.industry}
            nestedValue={setCompany}
            upperValue={company}
            containerClassName="formInputUnderline"
          />

          <InputTextNested
            title={
              <>
                <FaGlobe />
                Website
              </>
            }
            value={company?.website}
            name="website"
            placeholder="https://company.ca"
            nestedValue={setCompany}
            upperValue={company}
            containerClassName="formInputUnderline"
          />
        </CompanyInfoFieldRow>

        <CompanyContactSection>
          <CompanyContactHeader>
            <div>
              <span>Optional</span>
              <h3>Primary Company Contact</h3>
            </div>

            <CompanyContactOptional>
              Can be completed later
            </CompanyContactOptional>
          </CompanyContactHeader>

          <p>
            Add a primary contact for your company records if it is different
            from the manager account you just created. This does not create
            another user or provide login access.
          </p>

          <InputTextNested
            title={
              <>
                <FaUser />
                Contact Name
              </>
            }
            value={company?.mainContact}
            name="mainContact"
            placeholder="Contact name"
            nestedValue={setCompany}
            upperValue={company}
            containerClassName="formInputUnderline"
          />
        </CompanyContactSection>
      </CompanyInfoFields>

      {setStep && (
        <CompanyInfoActions>
          <button
            type="button"
            className="backButton"
            onClick={() => setStep((prev) => prev - 1)}
          >
            Back
          </button>

          <PrimaryButtonComponent
            span="Continue to Address"
            functionName={handleContinue}
            disabled={!isStepValid}
          />
        </CompanyInfoActions>
      )}
    </CompanyInfoContainer>
  );
};

CompanyInfo.propTypes = {
  company: PropTypes.shape({
    companyName: PropTypes.string,
    mainContact: PropTypes.string,
    email: PropTypes.string,
    phoneOffice: PropTypes.string,
    website: PropTypes.string,
    industry: PropTypes.string,
  }),
  setCompany: PropTypes.func,
  setStep: PropTypes.func,
};

export default CompanyInfo;
